import type { Finess } from '@sancare/typescript-commons/src/types/finess'
import {
  Annotation,
  BaseDrugEvent,
  CategoricalLabResult,
  DrugEvent,
  HealthConstant,
  LabResult,
  RawDrugEvent,
  TextualHealthEntryType
} from '@sancare/typescript-commons/src/types/health-data'
import { Dayjs } from 'dayjs'

import type { FalseJustificationItem, SuggestionJustification } from '@/misc/types'
import { Report, TextualHealthEntry } from '@/stay-displayer/health-data/types'
import { PredictedDiagnosis, PredictedMedicalAct } from '@/store/modules/diagnosis/types'
import { BaseAct, BaseCodedAct } from '@/store/modules/medical-act/types'
import { BasePatient, Patient, RawPatient } from '@/store/modules/patient/types'
import { User } from '@/store/modules/settings/types'

export enum StayStatusEnum {
  NEW = 'new',
  REVIEW = 'review',
  UNVALIDATED = 'unvalidated',
  VALIDATED = 'validated',
}

export enum ValidationTypeEnum {
  MANUAL = 'manual',
  AUTOMATION_AI = 'automation_ai',
  AUTOMATION_RULES = 'automation_rules',
  RECONDUCTION = 'reconduction',
}

export enum ExternalPmsiStatusEnum {
  NORMAL = 'normal',
  VALIDATED = 'validated',
  INVOICED = 'invoiced'
}

export enum StayNotificationEnum {
  NONE,
  NOT_READ,
  SOFT,
  HARD
}

export enum SeenStatusEnum {
  SEEN,
  UNSEEN,
  SEEN_AND_UPDATED
}

export type StayStatus = {
  notification: StayNotificationEnum
  statusValue: SeenStatusEnum
  user?: {
    username: string
  } | null
}

export interface BaseStay<TDate, TPatient extends BasePatient<TDate>, TDrugEvent extends BaseDrugEvent<TDate>> {
  id: number
  administrativeLocalStayId: number
  patient: TPatient
  patientAge: number
  stayStart: TDate
  stayEnd: TDate
  stayDuration: number
  lastPredictionUpdate: TDate | null
  lastHealthDataUpdate: TDate | null
  lastExternalPmsiUpdate: TDate | null
  status: StayStatusEnum
  validationDate?: string | null
  validationUser?: Pick<User, 'id' | 'firstName' | 'lastName' | 'sancareUser'> | null
  validationType?: ValidationTypeEnum | null
  validatedGroupName?: string | null
  reports: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
  categoricalLabResults: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
  drugEvents: TDrugEvent[]
  annotations: Annotation[]
  healthConstants: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
  labResults: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
  textualHealthEntries: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
  comment: string | null
  geographicEntity?: Finess | null
  legalEntity?: Finess | null
  suggestionJustifications?: Record<string, SuggestionJustification>
  keywordJustifications?: Record<string, SuggestionJustification>
  falseJustifications?: FalseJustificationItem[]
}
export type RawStay = BaseStay<string, RawPatient, RawDrugEvent>
export type Stay = BaseStay<Dayjs, Patient, DrugEvent>

export interface BaseSummaryUnit<
  TDate,
  TAct extends BaseAct,
  TCodedAct extends BaseCodedAct<TDate, TAct>> {
  id: number
  predictedLabels: (PredictedDiagnosis | PredictedMedicalAct)[]
  codedMedicalActs: TCodedAct[]
  startDate: TDate
  endDate: TDate
}

export interface GroupingError {
  code: number
  category: string
  description: string
  critical: boolean
}

export type StayHealthData = {
  reports: Report[]
  nurseReport: TextualHealthEntry[]
  targetNurseReport: TextualHealthEntry[]
  labResultComment: TextualHealthEntry[]
  medicalHistory: TextualHealthEntry[]
  surgeryLabel: TextualHealthEntry[]
  healthConstants: HealthConstant[]
  labResults: LabResult[]
  categoricalLabResults: CategoricalLabResult[]
  drugEvents: DrugEvent[]
  annotations: Annotation[]
  falseJustifications: FalseJustificationItem[]
}

export const formattedHealthData = (stay): StayHealthData => {
  const {
    reports,
    textualHealthEntries,
    healthConstants,
    labResults,
    categoricalLabResults,
    drugEvents,
    annotations,
    falseJustifications,
  } = stay

  return {
    reports: reports.filter((r) => r.content),
    nurseReport: textualHealthEntries.filter((t) => t.type === TextualHealthEntryType.NURSE_REPORT && t.content),
    targetNurseReport: textualHealthEntries.filter((t) => t.type === TextualHealthEntryType.TARGET_NURSE_REPORT && t.content),
    labResultComment: textualHealthEntries.filter((t) => t.type === TextualHealthEntryType.LAB_RESULT_COMMENT && t.content),
    medicalHistory: textualHealthEntries.filter((t) => t.type === TextualHealthEntryType.MEDICAL_HISTORY && t.content),
    surgeryLabel: textualHealthEntries.filter((t) => t.type === TextualHealthEntryType.SURGERY_LABEL && t.content),
    healthConstants: healthConstants,
    labResults: labResults,
    categoricalLabResults: categoricalLabResults.filter((r) => r.value),
    drugEvents: drugEvents,
    annotations: annotations,
    falseJustifications: falseJustifications ?? [],
  }
}
